// @givmo/step-up — running a gated act through the step-up refusal (AS-WEB3's gate, lifted at
// CCM-R1-STEP-UP; its behaviour for `step_up_required` is unchanged).
//
// Wraps one async operation so a `step_up_required` refusal becomes a prompt-and-retry instead of
// an error toast. So does a passkey confirmation refusal, as a passkey-only prompt, and any of the
// module's own `refusalCodes` (`stepUpRefusalAnswer`). The operation is a THUNK, which is the whole
// reason the person never loses work: the request payload stays captured in the caller's closure,
// so the retry re-sends exactly what was already typed, and the form is never asked to re-render,
// reset, or hand its values up.
//
//   mutationFn: (values) => gate.run(() => sdk.charityAdminSubmitCharityAch({ …values }))
//
// RETRY EXACTLY ONCE, un-wrapped. A second refusal after a completed re-authentication is a real
// disagreement (clock skew, a window that lapsed again, a gate the client cannot satisfy) and is
// surfaced as an ordinary error — the same retry-once discipline the api-client's own reauth replay
// keeps for the same reason: a self-feeding loop would re-prompt a person forever.
//
// THE TOKEN REFRESH IS THE LOAD-BEARING STEP between the two attempts. Re-authenticating updates
// the Firebase session, but the api-client's Bearer interceptor asks for the CACHED ID token;
// without a forced refresh the retry can carry the very token whose stale `auth_time` was just
// refused, and the person would be prompted, verify correctly, and be refused anyway. It lives here
// rather than in the dialog so that every route into a verified session — password, provider, a
// completed second-factor code, a passkey — passes through it, and it goes through the host's
// `refreshIdToken` so the LIVE session is refreshed rather than a captured render snapshot.
//
// WHICH WORDS (the row): the run's own `row`, else the row the refusal's code names in the gate's
// `refusalCodes` map, else the refusal's `context.action`, else the package fallback. A module whose
// refusal carries no `context.action` (Board's signing refusal) still gets each act's own words.
//
// READ FIRST, THEN OPEN (AS-WEB15). A host that reads the account only on demand (`prepare`) is
// asked to read when a refusal is about to open a prompt, and the gate waits for that read — never
// longer than `STEP_UP_PREPARE_WAIT_MS` — before it decides anything: the row (a module's row may
// name a fact, as adding a first passkey does the authenticator), the passkey-only dead end, and the
// sheet's arms all rest on the account's answer, not on what the page last cached or never read. A
// read that takes longer opens the sheet on today's answers, which follow the read when it lands.
// Once per prompt: a refusal that joins the host's prompt already open (P4) rests on the read that
// opened it; one that waits behind a prompt it cannot share reads once that prompt has closed, since
// the prompt it then opens is its own.
//
// ONE PROMPT PER HOST (CCM-R1-STEP-UP-B, P4): a `StepUpHostProvider` keeps the ONE prompt for every
// gate beneath it that names no host of its own, and draws the ONE sheet; such a gate's `dialog` is
// `null`. Two acts refused together under one provider share one prompt instead of stacking two
// sheets. Each request still carries its own words (`copy`, its row, as of its refusal), its own
// `testIdPrefix` and its own `refusalCodes`: the sheet shows the request that opened it, and only
// that prompt's own sheet settles it. A prompt the host cannot answer (no way it accepts is held
// here: the honest dead end) is shared only by requests that would dead-end too. A gate handed its
// own `host` keeps a prompt of its own, so no caller that passes one changes.
//
// A WAIT ENDS WITH ITS CALLER (P5): each `run` waiting on a prompt is registered, and so is one
// parked behind a prompt it cannot share. When the component that holds the gate unmounts, its waits
// reject with `StepUpCancelledError` (a parked one at once, not when the prompt it waits behind
// closes), and the prompt closes if no one else waits on it; when the provider (or a local gate)
// unmounts, every wait rejects. A run whose caller has gone never opens a prompt.
//
// WHILE THE PERSON IS ASKED (CCM-R1-STEP-UP-C): `prompting` is true exactly while one of the gate's
// runs waits on the person at a sheet: from asking for the prompt (opening it, joining the host's
// open one, or parking behind one it cannot share) until that wait ends, however it ends. Two
// windows after a refusal have the act pending with no sheet up, the wait on the host's `prepare`
// read and the forced token refresh before the retry; a caller that shows its act as busy on
// `pending && !prompting` covers both, and rests behind the sheet.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { StepUpDialog } from './step-up-dialog';
import { stepUpWords, type StepUpContext, type StepUpCopyTable } from './step-up-copy';
import {
  STEP_UP_WAYS_IN,
  StepUpHostContext,
  StepUpPasskeyFailure,
  acceptedStepUpWayIn,
  useOptionalStepUpHost,
  type StepUpHostAdapter,
  type StepUpWayIn,
} from './step-up-host';
import {
  stepUpActionOf,
  stepUpClientRefusalAnswer,
  stepUpRefusalAnswer,
  StepUpCancelledError,
  type StepUpClientRefusal,
  type StepUpRefusalAnswer,
  type StepUpRefusalCodes,
} from './step-up-refusal';

/**
 * The longest the gate waits on a host's `prepare` read before it opens the sheet anyway. The read is
 * one small request; this only bounds a stalled one, so a slow network never traps the act.
 */
export const STEP_UP_PREPARE_WAIT_MS = 3_000;

/** Resolve once `promise` settles or `ms` pass, whichever is first (never rejects). */
function settledWithin(promise: Promise<unknown>, ms: number): Promise<void> {
  return new Promise((resolve) => {
    const timer = setTimeout(resolve, ms);
    const done = () => {
      clearTimeout(timer);
      resolve();
    };
    promise.then(done, done);
  });
}

/** What one prompt asks for, beyond the refusal's own `action`. */
export interface StepUpPromptOptions {
  /**
   * The ways in this prompt accepts, by name. Default: every one ({@link STEP_UP_WAYS_IN}), so the
   * person meets it with whatever they hold.
   */
  accepts?: readonly StepUpWayIn[];
  /** What the prompt's words may name (the organization, its sign-in check). */
  context?: StepUpContext;
  /**
   * The row whose words this act's prompt shows. Wins over the row the refusal's code names and
   * over its `context.action`: the caller knows which act it is running.
   */
  row?: string;
}

/** What a prompt shows: the row, the run's options and the requesting gate's words and test ids. */
interface PromptRequest {
  readonly action: string | null;
  readonly options: StepUpPromptOptions;
  readonly copy: StepUpCopyTable;
  readonly testIdPrefix: string;
}

/** One gate (one `useStepUpGate` call), as long as its component is mounted. */
interface PromptOwner {
  live: boolean;
}

/** One run waiting on a prompt. */
interface Waiter {
  readonly owner: PromptOwner;
  readonly resolve: () => void;
  readonly reject: (reason: Error) => void;
}

/** The prompt that is open, everyone waiting on it, and a promise that settles when it closes. */
interface OpenPrompt {
  readonly request: PromptRequest;
  readonly accepts: readonly StepUpWayIn[];
  /** Whether the host holds a way this prompt accepts: only such a prompt is shared. */
  readonly answerable: boolean;
  readonly waiters: Set<Waiter>;
  readonly closed: Promise<void>;
  readonly close: () => void;
}

/** A host's ONE prompt: a local gate's own, or a provider's for every gate beneath it. */
interface StepUpPrompter {
  /** Whether the host's prompt is open now (a refusal then joins it, or waits behind it). */
  isOpen: () => boolean;
  /**
   * Wait for a prompt answering `request`; rejects once `owner` has gone. Resolves `true` once the
   * prompt it joined or opened is verified, and `false` once a prompt it waited behind (one it
   * cannot share) has closed: its gate then reads and decides again before it asks again.
   */
  request: (request: PromptRequest, owner: PromptOwner) => Promise<boolean>;
  /** End every wait `owner` holds (its component unmounted). */
  release: (owner: PromptOwner) => void;
}

export interface StepUpGate {
  /**
   * Run `operation`; on a step-up refusal, prompt for verification and run it once more. Rejects
   * with a `StepUpCancelledError` if the person dismisses the prompt, and with the operation's own
   * error otherwise — so a caller's existing failure handling keeps working untouched.
   */
  run: <T>(operation: () => Promise<T>, prompt?: StepUpPromptOptions) => Promise<T>;
  /**
   * Render this inside the caller. It is `null` unless a verification is outstanding, and always
   * `null` for a gate that joined its provider's prompt: the provider draws that sheet (P4).
   */
  dialog: ReactNode;
  /**
   * Whether one of this gate's runs is waiting on the person at a sheet now: from asking for the
   * prompt (opening it, joining the host's open one, or parking behind one it cannot share) until
   * that wait ends, verified, dismissed or cancelled. False during the first attempt, the host's
   * `prepare` read, the token refresh and the retry. Show an act as busy on its own pending state
   * and not this (`pending && !prompting`): busy through both windows a refusal leaves without a
   * sheet, at rest behind the sheet (CCM-R1-STEP-UP-C).
   */
  prompting: boolean;
}

export interface StepUpGateOptions {
  /** The module's rows, composed by the host: action → words. */
  copy: StepUpCopyTable;
  /** The test-id prefix the surface keeps (payout's: `payment-step-up`). */
  testIdPrefix: string;
  /** The host's half of the seam; else the nearest `StepUpHostProvider`'s. */
  host?: StepUpHostAdapter;
  /**
   * A module's own refusal codes this gate answers like `step_up_required` (Board's signing
   * refusal): a list, or a record naming each code's row — a fixed key, or a resolver reading the
   * error, which may also narrow the ways in (see `StepUpRefusalCodes`).
   */
  refusalCodes?: StepUpRefusalCodes;
  /**
   * A refusal the client raises itself, before any server could (AS-WEB14: Identity Platform's
   * recent-sign-in rule on an authenticator change, or a session whose first factor cannot make
   * it): answered like a named code, its ways narrowed by what it returns. The server's codes are
   * read first. Additive: a gate without it answers exactly what it answered before.
   */
  clientRefusal?: StepUpClientRefusal;
}

/**
 * The ONE prompt of a host, and the sheet that shows it (P4, P5). Concurrent requests share the open
 * prompt when every way in it accepts is one they accept too (verifying once satisfies each of them,
 * and stacked sheets would each own a popup); a request it cannot answer (a passkey-only act beside a
 * prompt that takes a password) waits for it to close, then asks on its own, never retrying on a way
 * it refuses.
 */
function useStepUpPrompter(host: StepUpHostAdapter): {
  prompter: StepUpPrompter;
  dialog: ReactNode;
} {
  // The ref is the source of truth (a request must read the CURRENT prompt, not a render-time
  // snapshot); the state exists only to drive rendering.
  const openRef = useRef<OpenPrompt | null>(null);
  // Runs parked behind a prompt they cannot share, so their caller's unmount can wake them (P5).
  const parkedRef = useRef(new Set<{ readonly owner: PromptOwner; readonly wake: () => void }>());
  const attemptRef = useRef(0);
  const [shown, setShown] = useState<{ prompt: OpenPrompt; attempt: number } | null>(null);
  // The host as of the latest render: whether it holds a way a prompt accepts.
  const hostRef = useRef(host);
  hostRef.current = host;
  const canAnswer = useCallback((ways: readonly StepUpWayIn[]): boolean => {
    const current = hostRef.current;
    return (
      (ways.includes('passkey') && current.passkey?.holdsPasskey === true) ||
      acceptedStepUpWayIn(current.waysIn, ways) !== undefined
    );
  }, []);

  const end = useCallback((prompt: OpenPrompt) => {
    if (openRef.current === prompt) {
      openRef.current = null;
      setShown(null);
    }
    prompt.close();
  }, []);

  const request = useCallback(
    async (next: PromptRequest, owner: PromptOwner): Promise<boolean> => {
      const accepts = next.options.accepts ?? STEP_UP_WAYS_IN;
      if (!owner.live) throw new StepUpCancelledError();
      const open = openRef.current;
      if (open !== null) {
        // A prompt the host cannot answer (no way it accepts is held here: the honest dead end) is
        // shared only by a request that would dead-end too: one this host CAN answer waits for its
        // own sheet rather than be cancelled with that one.
        if (
          open.accepts.every((way) => accepts.includes(way)) &&
          (open.answerable || !canAnswer(accepts))
        ) {
          await new Promise<void>((resolve, reject) => {
            open.waiters.add({ owner, resolve, reject });
          });
          return true;
        }
        // Parked until that prompt closes, or until this run's caller goes, whichever comes first;
        // then its gate decides again, on a read of its own (AS-WEB15).
        let wake!: () => void;
        const woken = new Promise<void>((resolveWoken) => {
          wake = resolveWoken;
        });
        const parked = { owner, wake };
        parkedRef.current.add(parked);
        try {
          await Promise.race([open.closed, woken]);
        } finally {
          parkedRef.current.delete(parked);
        }
        if (!owner.live) throw new StepUpCancelledError();
        return false;
      }
      let close!: () => void;
      const closed = new Promise<void>((resolveClosed) => {
        close = resolveClosed;
      });
      const prompt: OpenPrompt = {
        request: next,
        accepts,
        answerable: canAnswer(accepts),
        waiters: new Set(),
        closed,
        close,
      };
      openRef.current = prompt;
      // `attempt` keys the sheet so each opening mounts a FRESH one. Without it React can coalesce
      // a close and a re-open into one commit, leaving the previous sheet's typed password and
      // spent second-factor codes in place — the failure mode `apps/board`'s `use-step-up-prompt`
      // documents.
      attemptRef.current += 1;
      setShown({ prompt, attempt: attemptRef.current });
      await new Promise<void>((resolve, reject) => {
        prompt.waiters.add({ owner, resolve, reject });
      });
      return true;
    },
    [canAnswer],
  );

  // A prompt settles verified, cancelled, or failed with a typed reason the act surfaces (a
  // passkey-only prompt whose passkey turned out not to exist: `StepUpPasskeyFailure`). Only its OWN
  // sheet settles it: a ceremony still out when its prompt closed (its caller's page went, P5) may
  // finish after another prompt has opened, and must never answer that one.
  const settle = useCallback(
    (prompt: OpenPrompt, outcome: 'verified' | 'cancelled' | Error) => {
      if (openRef.current !== prompt) return;
      const waiters = [...prompt.waiters];
      prompt.waiters.clear();
      end(prompt);
      for (const waiter of waiters) {
        if (outcome === 'verified') waiter.resolve();
        else waiter.reject(outcome === 'cancelled' ? new StepUpCancelledError() : outcome);
      }
    },
    [end],
  );

  const release = useCallback(
    (owner: PromptOwner) => {
      // A run of this caller parked behind another prompt wakes, finds its caller gone, and rejects.
      for (const parked of parkedRef.current) if (parked.owner === owner) parked.wake();
      const prompt = openRef.current;
      if (prompt === null) return;
      for (const waiter of [...prompt.waiters]) {
        if (waiter.owner !== owner) continue;
        prompt.waiters.delete(waiter);
        waiter.reject(new StepUpCancelledError());
      }
      // No one left to answer for: the sheet closes rather than asking for nothing.
      if (prompt.waiters.size === 0) end(prompt);
    },
    [end],
  );

  // The host itself going ends every wait on it.
  useEffect(
    () => () => {
      const prompt = openRef.current;
      if (prompt === null) return;
      openRef.current = null;
      const waiters = [...prompt.waiters];
      prompt.waiters.clear();
      prompt.close();
      for (const waiter of waiters) waiter.reject(new StepUpCancelledError());
    },
    [],
  );

  const isOpen = useCallback(() => openRef.current !== null, []);
  const prompter = useMemo<StepUpPrompter>(
    () => ({ isOpen, request, release }),
    [isOpen, request, release],
  );
  const open = shown?.prompt ?? null;
  const dialog =
    shown === null || open === null ? null : (
      <StepUpDialog
        key={shown.attempt}
        host={host}
        words={stepUpWords(open.request.copy, open.request.action, open.request.options.context)}
        accepts={open.accepts}
        testIdPrefix={open.request.testIdPrefix}
        onVerified={() => settle(open, 'verified')}
        onCancel={() => settle(open, 'cancelled')}
        onFailed={(reason) => settle(open, reason)}
      />
    );
  return { prompter, dialog };
}

/** The provider's ONE prompt, for every gate beneath it that names no host of its own. */
const StepUpPromptContext = createContext<StepUpPrompter | null>(null);

/**
 * Provide the host's half of the seam to every gate beneath it, with the ONE prompt they share
 * (P4): the provider draws the sheet, and a gate that names no host of its own joins it.
 */
export function StepUpHostProvider({
  adapter,
  children,
}: {
  adapter: StepUpHostAdapter;
  children: ReactNode;
}) {
  const { prompter, dialog } = useStepUpPrompter(adapter);
  return (
    <StepUpHostContext.Provider value={adapter}>
      <StepUpPromptContext.Provider value={prompter}>
        {children}
        {dialog}
      </StepUpPromptContext.Provider>
    </StepUpHostContext.Provider>
  );
}

export function useStepUpGate({
  copy,
  testIdPrefix,
  host: given,
  refusalCodes,
  clientRefusal,
}: StepUpGateOptions): StepUpGate {
  const provided = useOptionalStepUpHost();
  const host = given ?? provided;
  if (host === null) {
    throw new Error('@givmo/step-up: no host (pass one, or render a StepUpHostProvider)');
  }
  // A gate handed its own host keeps its own prompt; one that names none joins its provider's.
  const shared = useContext(StepUpPromptContext);
  const local = useStepUpPrompter(host);
  const joined = given === undefined && shared !== null;
  const prompter = joined ? shared : local.prompter;
  // This gate, while its component is mounted (P5). Mounting again (React's StrictMode re-runs
  // effects) makes it live again.
  const ownerRef = useRef<PromptOwner>({ live: true });
  const owner = ownerRef.current;
  useEffect(() => {
    owner.live = true;
    return () => {
      owner.live = false;
      prompter.release(owner);
    };
  }, [owner, prompter]);
  // The host and the module's refusal readers as of the latest render, for a `run` that decides
  // before prompting — after a host's `prepare` read, on what that read rendered.
  const hostRef = useRef(host);
  hostRef.current = host;
  const refusalCodesRef = useRef(refusalCodes);
  refusalCodesRef.current = refusalCodes;
  const clientRefusalRef = useRef(clientRefusal);
  clientRefusalRef.current = clientRefusal;
  // The words and test ids as of the latest render: a request takes them when its refusal arrives,
  // so a caller that changes its words just before running (the passkey card names the passkey it
  // removes) shows them, as a gate drawing its own sheet always did.
  const copyRef = useRef(copy);
  copyRef.current = copy;
  const testIdPrefixRef = useRef(testIdPrefix);
  testIdPrefixRef.current = testIdPrefix;
  // How many of this gate's runs are waiting on the person at a sheet now.
  const [waitingOnPerson, setWaitingOnPerson] = useState(0);

  const refreshIdToken = host.refreshIdToken;
  const run = useCallback(
    async <T,>(operation: () => Promise<T>, options: StepUpPromptOptions = {}): Promise<T> => {
      try {
        return await operation();
      } catch (error) {
        const refusal = (): StepUpRefusalAnswer | null =>
          stepUpRefusalAnswer(error, refusalCodesRef.current) ??
          stepUpClientRefusalAnswer(error, clientRefusalRef.current);
        const first = refusal();
        if (first === null) throw error;
        for (;;) {
          // See the header: a host that reads on demand reads before a prompt opens, and everything
          // below rests on it — once per prompt: a refusal that joins the host's prompt already open
          // rests on the read that opened it, and one that waited behind a prompt reads again here.
          // P5: a caller already gone neither reads nor opens a prompt.
          if (!owner.live) throw new StepUpCancelledError();
          const prepare = hostRef.current.prepare;
          if (prepare !== undefined && !prompter.isOpen()) {
            await settledWithin(prepare(), STEP_UP_PREPARE_WAIT_MS);
            // P5: a caller that went during the read never opens a prompt.
            if (!owner.live) throw new StepUpCancelledError();
          }
          const answer = refusal() ?? first;
          const accepts = (options.accepts ?? STEP_UP_WAYS_IN).filter((way) =>
            answer.ways.includes(way),
          );
          // A prompt only the passkey can answer, for an account the host KNOWS holds none, could
          // only dead-end: fail the act with the typed reason instead, so its caller can offer to
          // set one up (Board's Sign does). The refusal rides along, so a caller that knows the act
          // can say its own words (the add flow, AS-PWL-2e).
          const current = hostRef.current;
          if (
            accepts.includes('passkey') &&
            current.passkey?.holdsPasskey === false &&
            acceptedStepUpWayIn(current.waysIn, accepts) === undefined
          ) {
            throw new StepUpPasskeyFailure('none_registered', error);
          }
          const row = options.row ?? answer.row ?? stepUpActionOf(error);
          // Waiting on the person from here until the wait ends, however it ends (a throw too).
          setWaitingOnPerson((count) => count + 1);
          let answered: boolean;
          try {
            answered = await prompter.request(
              {
                action: row,
                options: { ...options, accepts },
                copy: copyRef.current,
                testIdPrefix: testIdPrefixRef.current,
              },
              owner,
            );
          } finally {
            setWaitingOnPerson((count) => count - 1);
          }
          if (answered) break;
        }
        // See the header: the retry must not reuse the cached, already-refused token. Driven
        // through the host so it refreshes the LIVE session — not a render-state snapshot this
        // closure captured before the prompt was even shown.
        await refreshIdToken();
        return await operation();
      }
    },
    [prompter, owner, refreshIdToken],
  );

  return { run, dialog: joined ? null : local.dialog, prompting: waitingOnPerson > 0 };
}
